import { useEffect } from 'react';
import { useProjectFlowStore } from '../../stores';
import { getQueryParamValue } from 'src/utils/app';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUpdateProject, createProject } from '../../api/contentGenApis';
import { UpdateProjectRequest, CreateProjectRequest } from '../../api/apiTypes';
import { useSnackbar } from 'src/utils/Hooks';

interface UseProjectsOptions {
  /**
   * Whether to sync URL params with store on mount
   * @default true
   */
  syncUrlParams?: boolean;

  /**
   * Whether to redirect to projects list if no projectId is found
   * @default false
   */
  requireProjectId?: boolean;
}

export const useProjects = (options: UseProjectsOptions = {}) => {
  const { syncUrlParams = true, requireProjectId = false } = options;

  const { beaconNavigator } = useBeaconNavigator();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Get URL parameters
  const projectIdFromUrl = getQueryParamValue('projectId');
  const profileIdFromUrl = getQueryParamValue('profileId');
  const profileVersionFromUrl = Number(getQueryParamValue('profileVersion'));

  // Get project flow store
  const projectFlowStore = useProjectFlowStore();
  const {
    projectId: storedProjectId,
    projectName: storedProjectName,
    profileId: storedProfileId,
    profileName: storedProfileName,
    profileVersion: storedProfileVersion,
    setProjectDetails,
    clearProjectFlow
  } = projectFlowStore;

  const projectId = projectIdFromUrl || storedProjectId;
  const profileId = profileIdFromUrl || storedProfileId;
  
  const profileVersion = profileVersionFromUrl || storedProfileVersion;

  // Mutation for creating project
  const createProjectMutation = useMutation({
    mutationFn: (data: CreateProjectRequest) => createProject(data),
    onSuccess: () => {
      // Don't show success snackbar during project flow as user hasn't completed the entire flow yet
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
    onError: (error) => {
      showSnackbar({
        type: 'error',
        message: 'Failed to create project.'
      });
      console.error('Error creating project:', error);
    }
  });

  // Mutation for updating project
  const updateProjectMutation = useMutation({
    mutationFn: (data: UpdateProjectRequest) => apiUpdateProject(data),
    onSuccess: () => {
      showSnackbar({
        type: 'success',
        message: 'Project updated successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
    onError: (error) => {
      showSnackbar({
        type: 'error',
        message: 'Failed to update project.'
      });
      console.error('Error updating project:', error);
    }
  });

  useEffect(() => {
    if (syncUrlParams && projectIdFromUrl) {
     setProjectDetails({
        projectId: projectIdFromUrl,
        ...(profileIdFromUrl && { profileId: profileIdFromUrl })
      });
    }
  }, [syncUrlParams, projectIdFromUrl, profileIdFromUrl, setProjectDetails, storedProjectId, storedProfileId, storedProjectName, storedProfileName, profileVersion]);

  // Redirect to projects list if no projectId and it's required
  useEffect(() => {
    if (requireProjectId && !projectId) {
      beaconNavigator.updateSearchParams({
        tab: BEACON_TABS.PROJECTS,
        subtab: BEACON_SUBTABS.PROJECTS
      });
      beaconNavigator.go();
    }
  }, [requireProjectId, projectId, beaconNavigator]);

  // Navigation helpers
  const navigateToProjectSettings = (variant: 'create' | 'edit' = 'create') => {
    const effectiveProjectId = projectId || storedProjectId;
    const effectiveProfileId = profileId || storedProfileId;
    const isEdit = variant === 'edit' || effectiveProjectId; 

    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.PROJECTS,
      subtab: isEdit ? BEACON_SUBTABS.EXISTING_SETTING : BEACON_SUBTABS.PROJECTS_SETTINGS,
      ...(effectiveProjectId && { projectId: effectiveProjectId }),
      ...(effectiveProfileId && { profileId: effectiveProfileId })
    });
    beaconNavigator.go();
  };

  const navigateToProjectProducts = (overrideProjectId?: string, overrideProfileId?: string) => {
    const effectiveProfileId = overrideProfileId || profileId;

    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.PROJECTS,
      subtab: BEACON_SUBTABS.PROJECTS_PRODUCTS,
      ...(effectiveProfileId && { profileId: effectiveProfileId }),
      ...(overrideProjectId && { projectId: overrideProjectId })
    });
    beaconNavigator.go();
  };

  const navigateToProjectReview = (variant: 'create' | 'edit' = 'create') => {
    const effectiveProjectId = projectId || storedProjectId;
    const effectiveProfileId = profileId || storedProfileId;
    const isEdit = variant === 'edit';

    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.PROJECTS,
      subtab: isEdit ? BEACON_SUBTABS.EXISTING_REVIEW : BEACON_SUBTABS.PROJECTS_REVIEW,
      ...(effectiveProjectId && { projectId: effectiveProjectId }),
      ...(effectiveProfileId && { profileId: effectiveProfileId })
    });
    beaconNavigator.go();
  };

  const navigateToProjectsList = () => {
    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.PROJECTS,
      subtab: BEACON_SUBTABS.PROJECTS
    });
    beaconNavigator.go();
  };

  const cancelProjectFlow = () => {
    clearProjectFlow();
    navigateToProjectsList();
  };

  return {
    // State
    projectId,
    projectName: storedProjectName,
    profileId,
    profileName: storedProfileName,
    profileVersion,

    // Store methods
    setProjectDetails,
    clearProjectFlow,

    // Navigation methods
    navigateToProjectSettings,
    navigateToProjectProducts,
    navigateToProjectReview,
    navigateToProjectsList,
    cancelProjectFlow,

    // Mutations
    createProjectMutation,
    updateProjectMutation,

    // Beacon navigator for custom navigation
    beaconNavigator
  };
};
